// Producer financials route.
//
// Top KPI strip · cash-flow forecast chart · per-character breakdown · bulk
// payout trigger. Pulls milestones for the chart, contracts for the table,
// project for the totals.

import { copy } from '@ohear/shared'
import { Button, Card, EmptyState, Spinner } from '@ohear/ui'
import { createFileRoute } from '@tanstack/react-router'
import { useMemo, useState } from 'react'
import { ExportButtons, type ExportItem } from '../../../features/exports'
import { CashFlowChart, CharacterFinancialsTable } from '../../../features/financials'
import { useCharacters } from '../../../lib/api/characters'
import { useContracts, useTriggerPayouts } from '../../../lib/api/contracts'
import { bucketByMonth, useMilestones } from '../../../lib/api/milestones'
import { useProducerProject } from '../../../lib/api/producer-projects'
import { useRequireRole } from '../../../lib/auth'
import { useT } from '../../../lib/i18n/use-t'

export const Route = createFileRoute('/producer/projects/$projectId/financials')({
  component: FinancialsPage,
})

function FinancialsPage() {
  useRequireRole(['owner', 'member'])
  const t = useT()
  const { projectId } = Route.useParams()

  const projectQ = useProducerProject(projectId)
  const charactersQ = useCharacters(projectId)
  const contractsQ = useContracts(projectId)
  const milestonesQ = useMilestones(projectId)
  const triggerPayouts = useTriggerPayouts(projectId)

  const [busyContract, setBusyContract] = useState<string | null>(null)

  const project = projectQ.data
  const characters = charactersQ.data ?? []
  const contracts = contractsQ.data ?? []
  const milestones = milestonesQ.data ?? []

  const buckets = useMemo(() => bucketByMonth(milestones), [milestones])
  const currentMonth = new Date().toISOString().slice(0, 7)

  const projectedThisMonth = useMemo(() => {
    const now = currentMonth
    return milestones
      .filter((m) => (m.occurs_on ?? '').slice(0, 7) === now && m.kind !== 'invoice_due')
      .reduce((sum, m) => sum + m.amount_usd, 0)
  }, [milestones, currentMonth])

  const onTriggerPayout = async (contractId: string) => {
    // The endpoint is by-batch, but the table is by-contract. We trigger a
    // payout request scoped to the contract — server resolves the active
    // approved batches under that contract.
    setBusyContract(contractId)
    try {
      await triggerPayouts.mutateAsync({ batch_ids: [`contract:${contractId}`] })
    } finally {
      setBusyContract(null)
    }
  }

  if (projectQ.isLoading) {
    return (
      <Card>
        <div className="flex items-center gap-2 text-[13px] text-[var(--text-2)]">
          <Spinner /> Loading financials…
        </div>
      </Card>
    )
  }
  if (!project) {
    return (
      <Card>
        <p className="m-0 text-[13px] text-[var(--red)]">{t(copy.common.NOT_FOUND.project)}</p>
      </Card>
    )
  }

  const taxYear = new Date().getFullYear()
  const charNameById = new Map(characters.map((c) => [c.id, c.display_name]))
  const oneOhExports: ExportItem[] = contracts
    .filter((c) => c.actor_id)
    .map((c) => ({
      label: `1099 ${taxYear} - ${charNameById.get(c.character_id) ?? 'character'}`,
      format: 'pdf' as const,
      endpoint: '/api/v1/exports/1099',
      params: { actor_id: c.actor_id as string, tax_year: taxYear },
    }))
  const summaryExports: ExportItem[] = [
    {
      label: 'Project summary PDF',
      format: 'pdf',
      endpoint: '/api/v1/exports/project-summary',
      params: { project_id: projectId },
    },
  ]

  return (
    <section>
      <header className="mb-5 pb-4 border-b border-[var(--border)] flex flex-wrap items-end justify-between gap-3">
        <div className="flex flex-col gap-1 min-w-0">
          <div className="text-[12px] uppercase tracking-wide text-[var(--text-3)]">Financials</div>
          <h1 className="text-[22px] font-semibold tracking-tight text-[var(--text)] m-0 truncate">
            {project.name}
          </h1>
          <p className="text-[13px] text-[var(--text-2)] m-0">
            Cash-flow forecast and per-character payout status.
          </p>
        </div>
        <ExportButtons exports={[...summaryExports, ...oneOhExports]} />
      </header>

      <div className="mb-4 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <KpiCard label="Total project value" value={formatUsd(project.total_value_usd)} />
        <KpiCard label="Paid" value={formatUsd(project.paid_usd)} tone="green" />
        <KpiCard label="Outstanding" value={formatUsd(project.outstanding_usd)} tone="amber" />
        <KpiCard label="Projected this month" value={formatUsd(projectedThisMonth)} />
      </div>

      <div className="mb-4">
        <CashFlowChart buckets={buckets} currentMonth={currentMonth} />
      </div>

      <header className="mb-2 flex flex-wrap items-center justify-between gap-3">
        <h2 className="text-[16px] font-semibold text-[var(--text)]">Per-character status</h2>
        <Button
          variant="primary"
          loading={triggerPayouts.isPending}
          disabled={
            triggerPayouts.isPending ||
            contracts.every((c) => c.outstanding_usd <= 0 || c.status !== 'active')
          }
          onClick={() =>
            void triggerPayouts.mutateAsync({
              batch_ids: contracts
                .filter((c) => c.outstanding_usd > 0 && c.status === 'active')
                .map((c) => `contract:${c.id}`),
            })
          }
        >
          Trigger all approved payouts
        </Button>
      </header>

      {contracts.length === 0 ? (
        <EmptyState
          title="No contracts yet"
          description="Send characters for signing to populate the payout table."
        />
      ) : (
        <CharacterFinancialsTable
          characters={characters}
          contracts={contracts}
          onTriggerPayout={(id) => void onTriggerPayout(id)}
          busyContractId={busyContract}
        />
      )}
    </section>
  )
}

function KpiCard({
  label,
  value,
  tone,
}: {
  label: string
  value: string
  tone?: 'green' | 'amber'
}) {
  const valueColor =
    tone === 'green' ? 'var(--green)' : tone === 'amber' ? 'var(--amber)' : 'var(--text)'
  return (
    <Card>
      <div className="text-[11px] uppercase tracking-wide text-[var(--text-3)]">{label}</div>
      <div
        className="text-[22px] font-semibold tabular-nums leading-tight mt-0.5"
        style={{ color: valueColor }}
      >
        {value}
      </div>
    </Card>
  )
}

function formatUsd(value: number): string {
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(value)
}
